import HBIcon from "@/components/HBIcon";
import { useLogin } from "@/components/LoginModal";
import useAppStore from "@/context/store";
import { followDomain, unfollowDomain } from "@/services";
import isOverflowed from "@/utils/isOverflowed";
import { isFont } from "@/utils/pinMediaType";
import { Button } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import styles from "../index.module.less";
import { User2 } from "../User";

function PinTitle({ pin, setPin, showUser, showSource, showTooltip }) {
  const { login } = useLogin();
  const { user } = useAppStore(({ user }) => ({
    user,
  }));
  const [isExpand, setIsExpand] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [canLabelExpand, setCanLabelExpand] = useState(false);
  const titleRef = useRef(null);
  const [maxLen, setMaxLen] = useState(0);

  useEffect(() => {
    let domArr = Array.from(
      document.querySelectorAll(`.${styles.transparentItem}`)
    );
    // 判断元素有没有换行，找到收起状态只展示第一行
    let firstDomTop = domArr?.[0]?.getBoundingClientRect()?.top;
    let maxLen = 0;
    domArr.every((item, index) => {
      let domTop = item.getBoundingClientRect().top;
      // 找到第一个换行的元素，停止遍历
      if (domTop !== firstDomTop) {
        maxLen = index;
        return false;
      }
      maxLen = domArr.length;
      return true;
    });
    const canLabelExpand = pin?.tags?.length !== maxLen;
    setMaxLen(maxLen);
    // 判断标签是否超出一行
    setCanLabelExpand(canLabelExpand);

    // 判断是否展示展开按钮
    let canExpand = false;
    // 判断标题是否超出一行
    if (titleRef.current && isOverflowed(titleRef.current)) {
      canExpand = true;
    }
    // 判断标签是否超出一行
    if (canLabelExpand) {
      canExpand = true;
    }
    setCanExpand(canExpand);
  }, [pin]);

  // 标签当前展示项
  const showLabelList = useMemo(() => {
    if (isExpand) {
      return pin?.tags || [];
    } else {
      return pin?.tags?.slice(0, maxLen) || [];
    }
  }, [isExpand, maxLen, pin]);

  // 展开收起
  const onExpand = useCallback(() => {
    setIsExpand((isExpand) => !isExpand);
  }, []);

  // 获取标题文本：字体类型优先使用alias，否则使用raw_text
  const getTitleText = useCallback((pin) => {
    if (isFont(pin)) {
      const fontInfo =
        pin?.file_material?.font_info || pin?.material_info?.font_info;
      if (fontInfo?.alias) {
        return fontInfo.alias;
      }
    }
    return pin?.raw_text || "";
  }, []);

  const getRichTitle = useCallback((pin) => {
    let result = [];
    const titleText = getTitleText(pin);
    if (titleText) {
      let text = titleText;
      const mentions = pin?.text_meta?.mentions || []; // @用户
      const links = pin?.text_meta?.links || []; // 链接地址
      const richArr = [...mentions, ...links].sort((a, b) =>
        a.start < b.start ? -1 : 1
      );

      let start = 0;
      for (let rich of richArr) {
        if (typeof rich === "string") {
          continue;
        }
        result.push(text.substring(start, rich.start));
        start = rich.start;

        if (rich.mention_id) {
          // @用户
          let user = (
            <Link
              key={rich.mention_id}
              href={`/user/${rich.user?.urlname}`}
              target="_blank"
              onClick={(evt) => evt.stopPropagation()}
            >
              @{rich.user?.username}
            </Link>
          );
          result.push(user);
        } else {
          // Link链接
          const linkText = text.substring(start, start + rich.offset);
          if (
            /\b(huaban|gaoding|qingzhan|dancf|gd-share)\.(com|cn)\b/.test(
              linkText
            )
          ) {
            // 域名白名单
            let link = (
              <Link
                key={linkText}
                href={linkText}
                className={styles.link}
                target="_blank"
                onClick={(evt) => evt.stopPropagation()}
              >
                {/* <a target="_blank">{linkText}</a> */}
                点击查看
              </Link>
            );
            result.push(link);
          } else {
            result.push(linkText);
          }
        }

        start += rich.offset;
      }
      result.push(text.substring(start));
    }
    return result;
  }, []);

  const onToggleFollowDomain = async () => {
    if (!user) {
      const result = await login({
        source_location: "采集标题-关注域名",
      });
      if (!result) {
        return;
      }
    }

    if (pin?.domain?.following) {
      const result = await unfollowDomain({
        domain_id: pin.domain.domain_id,
      });
      if (!result.err) {
        setPin({
          ...pin,
          domain: {
            ...pin.domain,
            following: false,
          },
        });
      }
    } else {
      const result = await followDomain({ domain_id: pin.domain.domain_id });
      if (!result.err) {
        setPin({
          ...pin,
          domain: {
            ...pin.domain,
            following: true,
          },
        });
      }
    }
  };

  const titleText = getTitleText(pin);

  return (
    <div>
      <div className={styles.header}>
        {(titleText?.trim() || pin?.tags?.length > 0) && (
          <div className={styles.head}>
            <div className={styles.content}>
              <h1 className={styles.seo}>{titleText}</h1>
              <When condition={titleText?.trim()}>
                <div
                  className={classNames([
                    styles.title,
                    !isExpand ? styles.ellipsis : null,
                  ])}
                  ref={titleRef}
                  {...(!isExpand
                    ? {
                        onClick: onExpand,
                      }
                    : null)}
                >
                  {titleText ? getRichTitle(pin) : null}
                </div>
              </When>
              <When condition={pin?.tags?.length > 0}>
                <div className={styles.wrapper}>
                  <div
                    className={classNames(styles.label, {
                      [styles.expand]: isExpand,
                    })}
                  >
                    {showLabelList?.map((tag, index) => (
                      <span key={tag} className={styles.labelItem}>
                        <a
                          href={`/user/${pin?.user?.urlname}/tags/${tag}/pins`}
                          target="_blank"
                          className={styles.item}
                        >
                          {tag}
                        </a>
                        <When
                          condition={
                            index === showLabelList.length - 1 &&
                            canLabelExpand &&
                            !isExpand
                          }
                        >
                          <div className={styles.ellipsis}>...</div>
                        </When>
                      </span>
                    ))}
                  </div>
                  {/* 用于计算的组件不展示 */}
                  <div
                    className={classNames([styles.label, styles.transparent])}
                  >
                    {pin?.tags?.map((tag) => (
                      <span
                        className={classNames([
                          styles.item,
                          styles.transparentItem,
                        ])}
                        key={tag}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </When>
            </div>
            <When condition={canExpand}>
              <HBIcon
                className={classNames([
                  styles.icon,
                  isExpand ? styles.expand : null,
                ])}
                type="ic_arrow_down"
                onClick={onExpand}
              />
            </When>
          </div>
        )}
        <When
          condition={showSource && pin?.source && !pin?.domain?.hide_origin}
        >
          <div className={styles.from}>
            <div className={styles.left}>
              <HBIcon className={styles.icon} type="link" />
              <span className={styles.text}>来源</span>
              <a
                className={styles.link}
                href={`/go?pin_id=${pin?.pin_id}&url=${encodeURIComponent(
                  pin?.source
                )}`}
                target="_blank"
                rel="nofollow"
                title={pin?.source}
              >
                {pin?.source}
              </a>
            </div>
            <When condition={pin?.domain?.domain_id}>
              <div className={styles.right}>
                <Button
                  className={styles.button}
                  size="small"
                  type="text"
                  href={`/domains/${pin?.domain?.domain_id}`}
                  target="_blank"
                  rel="nofollow"
                  data-gd-click="button_click"
                  data-button-name="同来源"
                >
                  同来源
                </Button>
                <If condition={pin?.domain?.following}>
                  <Then>
                    <Button
                      className={styles.button}
                      size="small"
                      onClick={onToggleFollowDomain}
                    >
                      已关注
                    </Button>
                  </Then>
                  <Else>
                    <Button
                      className={styles.button}
                      size="small"
                      icon={<HBIcon type="ic_star" />}
                      onClick={onToggleFollowDomain}
                      data-gd-click="button_click"
                      data-button-name="关注来源"
                    >
                      关注来源
                    </Button>
                  </Else>
                </If>
              </div>
            </When>
          </div>
        </When>
        <When condition={showUser}>
          <User2 pin={pin} setPin={setPin}></User2>
        </When>
      </div>
    </div>
  );
}
export default memo(PinTitle);
